import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { gestApi, useGestAuth } from "./GestAuthContext";
import AlertsPanel from "./AlertsPanel";

const dt = (s) => s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" }) : "—";
const parseDate = (s) => s ? new Date(s) : null;
const daysBetween = (a, b) => Math.round((b.getTime() - a.getTime()) / 86400000);

function FaseRow({ fase, onEdit, onDelete, canEdit, min, max, totalDays }) {
  const start = parseDate(fase.data_inizio);
  const end = parseDate(fase.data_fine_prevista);
  const actual = parseDate(fase.data_fine_effettiva);
  const now = new Date();

  let offset = 0, width = 0;
  if (start && end && min && totalDays > 0) {
    offset = Math.max(0, Math.min(100, ((start - min) / (totalDays * 86400000)) * 100));
    const w = Math.max(0, ((end - start) / (totalDays * 86400000)) * 100);
    width = Math.max(2, Math.min(100 - offset, w));
  }

  const overdue = end && !actual && now > end;
  const done = !!actual;
  const active = start && end && start <= now && now <= end && !actual;
  const bg = done ? "#16a34a" : overdue ? "#dc2626" : active ? "#0a1420" : "#a7c8e9";
  const status = done ? "Completata" : overdue ? "In ritardo" : active ? "In corso" : "Pianificata";

  return (
    <div className="grid grid-cols-[1fr_180px] gap-4 items-center py-3 border-b border-black/5 last:border-b-0" data-testid={`gest-fase-row-${fase.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-[13px] font-medium text-[#0a1420] truncate">{fase.name}</div>
          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ color: bg, backgroundColor: bg + "1a" }}>{status}</span>
        </div>
        <div className="mt-1.5 h-2 bg-[#f5f7fb] rounded-full relative overflow-hidden">
          <div
            className="absolute top-0 h-2 rounded-full transition-all"
            style={{ left: `${offset}%`, width: `${width}%`, backgroundColor: bg }}
            title={`${dt(fase.data_inizio)} → ${dt(fase.data_fine_prevista)}`}
          />
        </div>
        <div className="text-[11px] text-[#6e6e73] mt-1">
          {dt(fase.data_inizio)} → {dt(fase.data_fine_prevista)}
          {actual && <span className="ml-2 text-green-700">✓ chiusa il {dt(fase.data_fine_effettiva)}</span>}
          {fase.note && <span className="ml-2 italic">· {fase.note}</span>}
        </div>
      </div>
      {canEdit && (
        <div className="flex gap-2 justify-end">
          <button onClick={() => onEdit(fase)} data-testid={`gest-fase-edit-${fase.id}`} className="text-[11px] text-[#0a1420] hover:underline">Modifica</button>
          <button onClick={() => onDelete(fase)} data-testid={`gest-fase-del-${fase.id}`} className="text-[11px] text-red-500 hover:text-red-700">Elimina</button>
        </div>
      )}
    </div>
  );
}

function FaseForm({ initial, cantiereId, onClose, onSaved }) {
  const isEdit = !!initial;
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    order: initial?.order ?? 99,
    data_inizio: (initial?.data_inizio || "").slice(0, 10),
    data_fine_prevista: (initial?.data_fine_prevista || "").slice(0, 10),
    data_fine_effettiva: (initial?.data_fine_effettiva || "").slice(0, 10),
    note: initial?.note || "",
  }));
  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    const payload = {
      ...form,
      order: Number(form.order || 0),
      data_inizio: form.data_inizio || null,
      data_fine_prevista: form.data_fine_prevista || null,
      data_fine_effettiva: form.data_fine_effettiva || null,
    };
    try {
      if (isEdit) {
        await gestApi.put(`/fasi/${initial.id}`, payload);
        toast.success("Fase aggiornata");
      } else {
        await gestApi.post(`/cantieri/${cantiereId}/fasi`, payload);
        toast.success("Fase aggiunta");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="w-full max-w-[520px] bg-white rounded-2xl overflow-hidden shadow-xl" data-testid="gest-fase-form">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <div className="text-[15px] font-semibold text-[#0a1420]">{isEdit ? "Modifica fase" : "Nuova fase"}</div>
          <button type="button" onClick={onClose} className="text-[#6e6e73] hover:text-[#0a1420] text-[20px] leading-none">×</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[12px] text-[#6e6e73] mb-1">Nome *</label>
            <input required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="gest-fase-name" className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] text-[#6e6e73] mb-1">Data inizio</label>
              <input type="date" value={form.data_inizio} onChange={(e) => update("data_inizio", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px]" />
            </div>
            <div>
              <label className="block text-[12px] text-[#6e6e73] mb-1">Data fine prevista</label>
              <input type="date" value={form.data_fine_prevista} onChange={(e) => update("data_fine_prevista", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px]" />
            </div>
          </div>
          <div>
            <label className="block text-[12px] text-[#6e6e73] mb-1">Data effettiva di chiusura (se completata)</label>
            <input type="date" value={form.data_fine_effettiva} onChange={(e) => update("data_fine_effettiva", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px]" />
          </div>
          <div className="grid grid-cols-[1fr_100px] gap-3">
            <div>
              <label className="block text-[12px] text-[#6e6e73] mb-1">Note</label>
              <input value={form.note} onChange={(e) => update("note", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px]" />
            </div>
            <div>
              <label className="block text-[12px] text-[#6e6e73] mb-1">Ordine</label>
              <input type="number" min="0" value={form.order} onChange={(e) => update("order", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] text-right" />
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-black/5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[13px] text-[#6e6e73]">Annulla</button>
          <button type="submit" disabled={busy} data-testid="gest-fase-save" className="px-5 py-2 bg-[#0a1420] text-white rounded-lg text-[13px] font-semibold disabled:opacity-60">
            {busy ? "Salvataggio…" : "Salva"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PredictionCard({ prediction, plannedDeadline }) {
  if (!prediction) return null;
  const conf = prediction.confidence;
  const est = prediction.estimated_end;
  const delay = prediction.delay_days;

  if (conf === "done") {
    return (
      <div className="bg-[#16a34a1a] border border-[#16a34a]/30 rounded-2xl p-5" data-testid="gest-prediction-card">
        <div className="text-[11px] text-[#0a1420]/70 uppercase tracking-widest">Previsione IA</div>
        <div className="mt-1 text-[16px] font-semibold text-green-700">Cantiere completato</div>
        <div className="text-[12px] text-[#6e6e73] mt-0.5">{prediction.reason}</div>
      </div>
    );
  }
  if (conf !== "ok") {
    return (
      <div className="bg-white rounded-2xl p-5 border border-black/[0.04] shadow-sm" data-testid="gest-prediction-card">
        <div className="text-[11px] text-[#6e6e73] uppercase tracking-widest">Previsione fine cantiere</div>
        <div className="mt-1 text-[16px] font-semibold text-[#0a1420]">Dati insufficienti</div>
        <div className="text-[12px] text-[#6e6e73] mt-0.5">{prediction.reason || "Non ancora abbastanza avanzamenti per stimare in modo affidabile."}</div>
      </div>
    );
  }
  const dOn = delay >= 0;
  const bg = delay > 0 ? "#dc2626" : delay < 0 ? "#16a34a" : "#0a1420";
  return (
    <div className="bg-white rounded-2xl p-5 border border-black/[0.04] shadow-sm" data-testid="gest-prediction-card">
      <div className="text-[11px] text-[#6e6e73] uppercase tracking-widest">Previsione fine cantiere</div>
      <div className="mt-1 flex items-center gap-3 flex-wrap">
        <div className="text-[22px] md:text-[26px] font-semibold text-[#0a1420] tracking-[-0.02em]">{dt(est)}</div>
        {delay !== null && delay !== undefined && (
          <div className="text-[12px] px-2 py-0.5 rounded-full font-medium" style={{ color: "#fff", backgroundColor: bg }}>
            {delay > 0 ? `+${delay} giorni di ritardo` : delay < 0 ? `${-delay} giorni di anticipo` : "In tempo"}
          </div>
        )}
      </div>
      {plannedDeadline && (
        <div className="text-[12px] text-[#6e6e73] mt-1">Consegna prevista: {dt(plannedDeadline)}</div>
      )}
      <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-black/5 text-[12px]">
        <div>
          <div className="text-[#6e6e73]">Ritmo produzione</div>
          <div className="text-[#0a1420] font-medium mt-0.5">
            {prediction.prod_rate_per_day ? `${prediction.prod_rate_per_day.toLocaleString("it-IT", { maximumFractionDigits: 2 })} pz/giorno` : "—"}
          </div>
        </div>
        <div>
          <div className="text-[#6e6e73]">Ritmo posa</div>
          <div className="text-[#0a1420] font-medium mt-0.5">
            {prediction.posa_rate_per_day ? `${prediction.posa_rate_per_day.toLocaleString("it-IT", { maximumFractionDigits: 2 })} pz/giorno` : "—"}
          </div>
        </div>
      </div>
      {prediction.reason && (
        <div className="text-[11px] italic text-[#6e6e73] mt-3">{prediction.reason}</div>
      )}
    </div>
  );
}

export default function GestCronoprogramma({ cantiere, onCantiereRefresh }) {
  const { user } = useGestAuth();
  const canEdit = user?.role === "admin" || user?.role === "ufficio";
  const [fasi, setFasi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await gestApi.get(`/cantieri/${cantiere.id}/fasi`);
      setFasi(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setLoading(false); }
  }, [cantiere.id]);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    if (!window.confirm("Creare le 6 fasi standard (progettazione, materiali, produzione, consegna, posa, collaudo) distribuite sulle date del cantiere?")) return;
    try {
      const { data } = await gestApi.post(`/cantieri/${cantiere.id}/fasi/seed`, {});
      toast.success(`${data.count} fasi create`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const del = async (f) => {
    if (!window.confirm(`Eliminare la fase "${f.name}"?`)) return;
    try {
      await gestApi.delete(`/fasi/${f.id}`);
      toast.success("Fase eliminata");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // Compute timeline scale (min/max dates)
  const dates = fasi.flatMap((f) => [parseDate(f.data_inizio), parseDate(f.data_fine_prevista)]).filter(Boolean);
  const min = dates.length ? new Date(Math.min(...dates.map((d) => d.getTime()))) : null;
  const max = dates.length ? new Date(Math.max(...dates.map((d) => d.getTime()))) : null;
  const totalDays = min && max ? Math.max(1, daysBetween(min, max)) : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="gest-cronoprogramma">
      <div className="lg:col-span-2 bg-white rounded-2xl border border-black/[0.04] shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[13px] font-semibold text-[#0a1420]">Fasi cantiere</div>
            <div className="text-[11px] text-[#6e6e73] mt-0.5">
              {min && max ? `${dt(min)} — ${dt(max)} · ${totalDays} giorni` : "Aggiungi le fasi per costruire la timeline"}
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              {fasi.length === 0 && (
                <button onClick={seed} data-testid="gest-fase-seed" className="text-[12px] px-3 py-1.5 border border-black/10 rounded-lg hover:bg-[#f5f7fb]">
                  Usa fasi standard
                </button>
              )}
              <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="gest-fase-new" className="text-[12px] px-3 py-1.5 bg-[#0a1420] text-white rounded-lg hover:bg-[#1a2432]">
                + Nuova fase
              </button>
            </div>
          )}
        </div>
        {loading ? (
          <div className="p-6 text-[#6e6e73] text-[13px]">Caricamento…</div>
        ) : fasi.length === 0 ? (
          <div className="p-8 text-center text-[13px] text-[#6e6e73]">
            <div className="text-[26px] mb-2 opacity-40">◷</div>
            <div>Nessuna fase pianificata.</div>
            {canEdit && <div className="text-[12px] mt-1">Clicca «Usa fasi standard» per iniziare in un click.</div>}
          </div>
        ) : (
          <div className="px-5 py-2">
            {fasi.map((f) => (
              <FaseRow
                key={f.id}
                fase={f}
                onEdit={(x) => { setEditing(x); setShowForm(true); }}
                onDelete={del}
                canEdit={canEdit}
                min={min}
                max={max}
                totalDays={totalDays}
              />
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <PredictionCard prediction={cantiere.prediction} plannedDeadline={cantiere.data_consegna_prevista} />
        <div className="bg-white rounded-2xl border border-black/[0.04] shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-black/5 text-[13px] font-semibold text-[#0a1420]">Alert di questo cantiere</div>
          <AlertsPanel alerts={cantiere.alerts || []} variant="compact" linkToCantiere={false} emptyLabel="Tutto sotto controllo" />
        </div>
      </div>

      {showForm && (
        <FaseForm
          initial={editing}
          cantiereId={cantiere.id}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { load(); onCantiereRefresh?.(); }}
        />
      )}
    </div>
  );
}
